import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { Building, Plus, Calculator, AlertCircle, CheckCircle } from "lucide-react";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const SquareFootageManager = ({ calculationId, onLocationsUpdate, onAllocationsUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({
    location_name: "",
    total_square_footage: "",
    monthly_rent: "",
    monthly_utilities: "",
    monthly_other_occupancy: ""
  });

  useEffect(() => { loadLocations(); }, []);

  const loadLocations = async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(`${API}/square-footage-locations`);
      const list = res.data.square_footage_locations || [];
      setLocations(list);
      onLocationsUpdate && onLocationsUpdate(list);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error loading locations');
    } finally {
      setLoading(false);
    }
  };

  const addLocation = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const payload = {
        location_name: form.location_name,
        total_square_footage: parseFloat(form.total_square_footage)||0,
        monthly_rent: parseFloat(form.monthly_rent)||0,
        monthly_utilities: parseFloat(form.monthly_utilities)||0,
        monthly_other_occupancy: parseFloat(form.monthly_other_occupancy)||0
      };
      const res = await axios.post(`${API}/square-footage-locations`, payload);
      if (res.data.success) {
        setSuccess('Location added');
        setForm({ location_name: "", total_square_footage: "", monthly_rent: "", monthly_utilities: "", monthly_other_occupancy: ""});
        await loadLocations();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error adding location');
    } finally {
      setSaving(false);
    }
  };

  const calculate = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const res = await axios.post(`${API}/calculate-square-footage-allocation/${calculationId}`);
      if (res.data.success) {
        setSuccess('Square footage allocation calculated');
        onAllocationsUpdate && onAllocationsUpdate({ allocation_results: res.data.allocation_results });
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error calculating allocation');
    } finally { setSaving(false); }
  };

  if (loading) return (<div className="flex items-center justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"/><span className="ml-2">Loading locations...</span></div>);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Building className="h-5 w-5 text-indigo-600"/> Square Footage</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <Alert className="border-red-200 bg-red-50 mb-4"><AlertCircle className="h-4 w-4"/><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>}
        {success && <Alert className="border-green-200 bg-green-50 mb-4"><CheckCircle className="h-4 w-4"/><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>}

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
          <div className="md:col-span-2">
            <Label>Location Name</Label>
            <Input value={form.location_name} onChange={e=>setForm({...form, location_name:e.target.value})} placeholder="HQ / West Office"/>
          </div>
          <div>
            <Label>Sq Ft</Label>
            <Input type="number" value={form.total_square_footage} onChange={e=>setForm({...form, total_square_footage:e.target.value})}/>
          </div>
          <div>
            <Label>Rent/mo</Label>
            <Input type="number" value={form.monthly_rent} onChange={e=>setForm({...form, monthly_rent:e.target.value})}/>
          </div>
          <div>
            <Label>Utilities/mo</Label>
            <Input type="number" value={form.monthly_utilities} onChange={e=>setForm({...form, monthly_utilities:e.target.value})}/>
          </div>
          <div>
            <Label>Other/mo</Label>
            <Input type="number" value={form.monthly_other_occupancy} onChange={e=>setForm({...form, monthly_other_occupancy:e.target.value})}/>
          </div>
        </div>
        <div className="flex gap-2 mb-6">
          <Button onClick={addLocation} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1"/>Add Location</Button>
          <Button onClick={calculate} disabled={saving || !calculationId} variant="outline"><Calculator className="h-4 w-4 mr-1"/>Calculate Allocation</Button>
        </div>

        <Separator className="mb-4"/>

        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="text-right">Sq Ft</TableHead>
                <TableHead className="text-right">Rent/mo</TableHead>
                <TableHead className="text-right">Utilities/mo</TableHead>
                <TableHead className="text-right">Other/mo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {locations.map((l) => (
                <TableRow key={l._id}>
                  <TableCell className="font-medium">{l.location_name}</TableCell>
                  <TableCell className="text-right">{Number(l.total_square_footage||0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">${Number(l.monthly_rent||0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">${Number(l.monthly_utilities||0).toLocaleString()}</TableCell>
                  <TableCell className="text-right">${Number(l.monthly_other_occupancy||0).toLocaleString()}</TableCell>
                </TableRow>
              ))}
              {locations.length===0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-500">No locations yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default SquareFootageManager;